import React, { useState } from 'react';
import { getUsersForClient } from '../../../services/adminService';
import type { User } from '../../../types/auth';
import type { Section } from '../../../types/tender';

export function useSectionAssignments(
  account: User | null,
  assign: (section: Section, user: User, permissions?: string[]) => Promise<void>
) {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [sectionToAssign, setSectionToAssign] = useState<Section | null>(null);
  const [teamUsers, setTeamUsers] = useState<User[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const assignAbortControllerRef = React.useRef<AbortController | null>(null);

  const handleOpenAssignDialog = (section: Section) => {
    setSectionToAssign(section);
    setAssignDialogOpen(true);

    if (account?.clientId) {
      if (assignAbortControllerRef.current) {
        assignAbortControllerRef.current.abort();
      }
      const controller = new AbortController();
      assignAbortControllerRef.current = controller;

      setAssignLoading(true);
      getUsersForClient(account.clientId, controller.signal)
        .then((res) => {
          if (res.success && res.users) {
            setTeamUsers(res.users);
          } else {
            setTeamUsers([]);
          }
        })
        .catch((_err) => {
          if (
            _err.name === 'CanceledError' ||
            _err.name === 'AbortError' ||
            _err.code === 'ERR_CANCELED'
          ) {
            return;
          }
          setTeamUsers([]);
        })
        .finally(() => {
          if (assignAbortControllerRef.current === controller) {
            setAssignLoading(false);
            assignAbortControllerRef.current = null;
          }
        });
    }
  };

  const handleAssignUser = (user: User, permissions?: string[]) => {
    if (sectionToAssign) {
      assign(sectionToAssign, user, permissions);
      setAssignDialogOpen(false);
      setSectionToAssign(null);
    }
  };

  return {
    assignDialogOpen,
    setAssignDialogOpen,
    sectionToAssign,
    teamUsers,
    assignLoading,
    handleOpenAssignDialog,
    handleAssignUser,
  };
}
